import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material'
import {
  Assessment,
  TrendingUp,
  People,
  Folder,
  VideoCall,
  Download,
  Description,
} from '@mui/icons-material'
import {
  useGetReportsDashboardQuery,
  useLazyExportScoresCsvQuery,
  useLazyExportSummaryTxtQuery,
  useLazyExportSummaryPdfQuery,
} from '../store/api/reportsApi'
import { PageHeader } from '../components/PageHeader'
import { getCardSurfaceSx, getContentPaperSx, getTablePaperSx } from '../theme/sectionSurfaces'

export default function ReportsPage() {
  const { data, isLoading, isError, error } = useGetReportsDashboardQuery()
  const [exportCsv] = useLazyExportScoresCsvQuery()
  const [exportTxt] = useLazyExportSummaryTxtQuery()
  const [exportPdf] = useLazyExportSummaryPdfQuery()

  const handleExportCsv = async () => {
    try {
      const blob = await exportCsv().unwrap()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'scores_export.csv'
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (e: any) {
      alert(e?.data?.detail || e?.message || 'Xuất CSV thất bại')
    }
  }

  const handleExportPdf = async () => {
    try {
      const blob = await exportPdf().unwrap()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'bao_cao_tom_tat.pdf'
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (e: any) {
      alert(e?.data?.detail || e?.message || 'Xuất PDF thất bại')
    }
  }

  const handleExportTxt = async () => {
    try {
      const text = await exportTxt().unwrap()
      const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'bao_cao_tom_tat.txt'
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (e: any) {
      alert(e?.data?.detail || e?.message || 'Xuất báo cáo thất bại')
    }
  }

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 4 } }}>
      <PageHeader
        title="Báo cáo và Phân tích"
        subtitle="Tổng quan điểm số, tham gia courtroom và xuất báo cáo cho Ban Thư ký / Giám khảo."
        icon={<Assessment />}
        mb={4}
        actions={
          <>
            <Button variant="outlined" color="primary" startIcon={<Download />} onClick={handleExportCsv}>
              Xuất CSV điểm
            </Button>
            <Button variant="outlined" color="primary" startIcon={<Description />} onClick={handleExportTxt}>
              Xuất báo cáo TXT
            </Button>
            <Button variant="contained" color="primary" startIcon={<Download />} onClick={handleExportPdf}>
              Xuất PDF
            </Button>
          </>
        }
      />

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {(error as any)?.data?.detail || 'Không tải được thống kê (kiểm tra quyền truy cập).'}
        </Alert>
      )}

      {data && (
        <>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={0} sx={(theme) => getCardSurfaceSx(theme)}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Cuộc thi (phạm vi quyền)
                      </Typography>
                      <Typography variant="h4" fontWeight="bold">
                        {data.total_cases}
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                      <Folder />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={0} sx={(theme) => getCardSurfaceSx(theme)}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Tổng đội (hệ thống)
                      </Typography>
                      <Typography variant="h4" fontWeight="bold">
                        {data.total_teams}
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'secondary.main', width: 56, height: 56 }}>
                      <People />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={0} sx={(theme) => getCardSurfaceSx(theme)}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Phiên courtroom (phạm vi)
                      </Typography>
                      <Typography variant="h4" fontWeight="bold">
                        {data.total_courtrooms}
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'info.main', width: 56, height: 56 }}>
                      <VideoCall />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={0} sx={(theme) => getCardSurfaceSx(theme)}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Điểm TB (phiếu đã gửi)
                      </Typography>
                      <Typography variant="h4" fontWeight="bold">
                        {data.average_total_score != null ? data.average_total_score : '—'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Phiếu đã gửi: {data.submitted_scores_count}
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'success.main', width: 56, height: 56 }}>
                      <TrendingUp />
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Paper elevation={0} sx={(theme) => ({ ...getContentPaperSx(theme), p: 3 })}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Bảng xếp hạng (theo điểm trung bình phiếu đã gửi)
            </Typography>
            <TableContainer sx={(theme) => getTablePaperSx(theme)}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Đội</TableCell>
                    <TableCell align="right">Điểm TB</TableCell>
                    <TableCell align="right">Số phiếu</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.leaderboard || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography color="text.secondary">Chưa có dữ liệu xếp hạng.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.leaderboard.map((row, idx) => (
                      <TableRow key={row.team_id} hover>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>{row.team_name}</TableCell>
                        <TableCell align="right">
                          <Chip label={row.avg_score} size="small" color="primary" />
                        </TableCell>
                        <TableCell align="right">{row.score_count}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Container>
  )
}
